import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { whenEscapeButtonPressed } from '@utils/interactionEvents';
import {
  Wrapper,
  ImageWrapper,
  Image,
  ZoomIcon,
  Modal,
  ModalContent,
  ModalImage,
  CloseButton,
  CloseIcon,
} from './ZoomableImage.styled';

interface ZoomableImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt?: string;
  className?: string;
  dataTestId?: string;
}

const ZoomableImage: React.FC<ZoomableImageProps> = ({
  src,
  alt = '',
  className,
  dataTestId = 'zoomable-image',
  ...imageProps
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Only close if clicking the modal backdrop, not the image or content
      if (e.target === e.currentTarget) {
        handleCloseModal();
      }
    },
    [handleCloseModal],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      whenEscapeButtonPressed(handleCloseModal)(e);
    },
    [handleCloseModal],
  );

  useEffect(() => {
    if (!isModalOpen) return;

    globalThis.addEventListener('keydown', onKeyDown);

    return () => globalThis.removeEventListener('keydown', onKeyDown);
  }, [isModalOpen, onKeyDown]);

  return (
    <>
      <Wrapper className={className} data-testid={dataTestId}>
        <ImageWrapper onClick={handleOpenModal}>
          <Image
            src={src}
            alt={alt}
            data-testid={`${dataTestId}__image`}
            {...imageProps}
          />
          <ZoomIcon
            name="fullscreen_0"
            data-testid={`${dataTestId}__zoom-icon`}
          />
        </ImageWrapper>
      </Wrapper>

      {isModalOpen &&
        createPortal(
          <Modal
            data-testid={`${dataTestId}__modal`}
            onClick={handleBackdropClick}
          >
            <ModalContent
              onClick={(e: React.MouseEvent<HTMLDivElement>) =>
                e.stopPropagation()
              }
            >
              <CloseButton
                onClick={handleCloseModal}
                data-testid={`${dataTestId}__close-button`}
              >
                <CloseIcon name="close_0" />
              </CloseButton>
              <ModalImage
                src={src}
                alt={alt}
                data-testid={`${dataTestId}__modal-image`}
              />
            </ModalContent>
          </Modal>,
          document.body,
        )}
    </>
  );
};

export default ZoomableImage;
